import { FC, useMemo } from "react";
import { Facility } from "../../models";
import { FacilityEvent } from "../../models/FacilityEvent.gen";
import { Target } from "../../models/Target.gen";
import { getConfiguredLaneName } from "../../models/Shared.Lane.helpers";
import { targetDescribesEvent } from "./eventDetailsHelpers";
import { useDialog } from "@app/shared";
import JsonViewer from "@app/dashboard/src/components/shared/JsonViewer";

interface EventTargetJsonViewProps {
    facility: Facility;
    target: Target | undefined;
    event?: FacilityEvent;
    /**
     * Gate for the link. The dashboard passes its super-admin check; the firmware web config is
     * already an on-device admin surface, so it passes true.
     */
    enabled?: boolean;
}

/**
 * "View JSON" link plus the dialog it opens, showing the event and the target as separate objects
 * with the event on top.
 *
 * Shared because the dashboard (TargetDetailsDialog) and the firmware web config
 * (TargetDetailsPanel) are parallel detail views: without this the link existed only on the
 * dashboard, and diagnosing a device-side event meant leaving the device UI.
 */
export const EventTargetJsonView: FC<EventTargetJsonViewProps> = ({ facility, target, event, enabled = true }) => {
    const jsonDialog = useDialog<{ event?: FacilityEvent; target: Target | undefined }>();

    // The dashboard's event carries the whole target inline — CoreApi.Models.FacilityEvent has a
    // `Target` property — so dumping the event verbatim printed the target twice: once nested under
    // Event, then again in full under Target. The device's own /api/events attaches no target, which
    // is why the firmware web config never showed the duplicate. Strip it so both surfaces read the
    // same; the Target section below is where a target belongs.
    // Strip the key whether it is populated or null — the org-events payload sends `target: null`, and
    // leaving that behind still shows a dead "target": NULL line the device UI doesn't have.
    const eventJson = useMemo(() => {
        if (!event || !("target" in event)) return event;
        const withoutTarget = { ...event };
        delete (withoutTarget as { target?: unknown }).target;
        return withoutTarget as FacilityEvent;
    }, [event]);

    // Prefer the separately loaded target — it comes with history, the event's copy doesn't — but fall
    // back to the event's own, so stripping above can never hide a target outright rather than move it.
    const targetForJson = target ?? event?.target;

    // The raw Target carries only the `lanes` bitmask, not the configured lane name the tables and
    // detail cards show. Enrich the dump so what you read here matches what the UI displays.
    const targetJson = useMemo(() => {
        if (!targetForJson) return targetForJson;
        const configuredLane = getConfiguredLaneName(
            targetForJson.lanes,
            facility.laneLines?.filter((ll) => !ll.deviceId || ll.deviceId === targetForJson.deviceId)
        );
        return configuredLane ? { ...targetForJson, configuredLaneName: configuredLane.primary } : targetForJson;
    }, [targetForJson, facility.laneLines]);

    // Nothing to show without at least one of the two objects.
    if (!enabled || (!target && !event)) return null;

    return (
        <>
            <div className="text-center pt-2">
                <a
                    href="#"
                    className="text-xs text-brand-primary hover:underline focus:outline-none"
                    onClick={(e) => {
                        e.preventDefault();
                        jsonDialog.show("JSON", "Close", { event: eventJson, target: targetJson as Target }, () => {});
                    }}
                >
                    View JSON
                </a>
            </div>

            {jsonDialog.renderDialog(
                (data) => (
                    <div className="max-h-[60vh] overflow-auto space-y-4">
                        {data?.event && (
                            <div>
                                <div className="text-sm font-semibold pb-1">Event</div>
                                <JsonViewer data={data.event} />
                            </div>
                        )}
                        {data?.target && (
                            <div>
                                {/* The cards above hide target details for events where the attached
                                    target doesn't describe the event (Presence). The raw payload is
                                    still shown here — this is the debugging view — but labelled, so
                                    it can't be mistaken for a representative occupant. */}
                                <div className="text-sm font-semibold pb-1">
                                    {targetDescribesEvent(data.event) ? "Target" : "Target that triggered this event"}
                                </div>
                                <JsonViewer data={data.target} />
                            </div>
                        )}
                    </div>
                ),
                {
                    // Width only, no fixed height. Dialog's inner stack (title/content/footer) has no
                    // h-full, so an h-[75%] on the modal left the leftover height as blank white below
                    // the footer whenever the JSON was short. The body below caps and scrolls itself,
                    // which is also the convention the other dialogs here use (max-h, not h).
                    className: "w-[75%] overflow-hidden",
                    closeOnOutsideClick: true,
                    // This is a read-only viewer, so "Close" is the only action. renderDialog adds a
                    // Cancel button whenever an onOk handler is supplied, which gave two buttons that
                    // did the same thing.
                    showCancelButton: false,
                }
            )}
        </>
    );
};
